/**
 * Client-side cache of GET /users/me/api-access (endpoint rules computed by the
 * backend from the user's DB role permissions).
 *
 * The ApiClient consults this before every request so the app never fires a call
 * the user's role cannot make. Fail-open by design: with no rules loaded (older
 * backend, fetch failure, super-admin platform session) every call goes through —
 * the server middleware remains the enforcement point.
 */
import { shouldAllowPortalAssignmentSubmit } from '../../config/portalAssignmentApiAccess';
import { shouldAllowPortalFeeSelfRead } from '../../config/portalFeeApiAccess';
import type { UserApiAccessRule } from './types/rbac';

const STORAGE_KEY = 'rust-school-api-access';

type StoredRule = Pick<UserApiAccessRule, 'method' | 'path' | 'allowed'>;

type IndexedRule = StoredRule & { segments: string[]; literals: number };

/** method -> segment count -> rules (path params match any segment). */
let ruleIndex: Map<string, Map<number, IndexedRule[]>> | null = null;

function indexRules(rules: StoredRule[]): Map<string, Map<number, IndexedRule[]>> {
  const index = new Map<string, Map<number, IndexedRule[]>>();
  for (const rule of rules) {
    const segments = rule.path.split('/').filter(Boolean);
    const indexed: IndexedRule = {
      ...rule,
      segments,
      literals: segments.filter((segment) => !segment.startsWith(':')).length,
    };
    const method = rule.method.toUpperCase();
    const byCount = index.get(method) ?? new Map<number, IndexedRule[]>();
    const bucket = byCount.get(segments.length) ?? [];
    bucket.push(indexed);
    byCount.set(segments.length, bucket);
    index.set(method, byCount);
  }
  return index;
}

function readStoredRules(): StoredRule[] {
  try {
    let raw =
      typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_KEY) : null;
    if (!raw && typeof sessionStorage !== 'undefined') {
      raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw && typeof localStorage !== 'undefined') {
        try {
          localStorage.setItem(STORAGE_KEY, raw);
          sessionStorage.removeItem(STORAGE_KEY);
        } catch {
          /* ignore migrate failures */
        }
      }
    }
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as StoredRule[]) : [];
  } catch {
    return [];
  }
}

export function setApiAccessRules(rules: UserApiAccessRule[]): void {
  const compact: StoredRule[] = rules.map(({ method, path, allowed }) => ({
    method,
    path,
    allowed,
  }));
  ruleIndex = indexRules(compact);
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(compact));
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* storage full/unavailable — in-memory index still applies */
  }
}

export function clearApiAccessRules(): void {
  ruleIndex = null;
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem(STORAGE_KEY);
    }
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

function activeIndex(): Map<string, Map<number, IndexedRule[]>> | null {
  if (ruleIndex) return ruleIndex;
  const stored = readStoredRules();
  if (!stored.length) return null;
  ruleIndex = indexRules(stored);
  return ruleIndex;
}

function normalizePathSegments(endpoint: string): string[] {
  const path = endpoint.split('?')[0].split('#')[0];
  return path.split('/').filter(Boolean);
}

function matchRule(method: string, endpoint: string): IndexedRule | null {
  const index = activeIndex();
  if (!index) return null;
  const byCount = index.get(method.toUpperCase());
  if (!byCount) return null;
  const segments = normalizePathSegments(endpoint);
  const candidates = byCount.get(segments.length);
  if (!candidates?.length) return null;

  let best: IndexedRule | null = null;
  for (const rule of candidates) {
    let ok = true;
    for (let i = 0; i < segments.length; i += 1) {
      const pattern = rule.segments[i];
      if (pattern.startsWith(':')) continue;
      if (pattern !== decodeURIComponent(segments[i])) {
        ok = false;
        break;
      }
    }
    // Most-literal match wins (e.g. /students/export over /students/:id).
    if (ok && (!best || rule.literals > best.literals)) {
      best = rule;
    }
  }
  return best;
}

/**
 * True when the call may be fired. Unknown endpoints and empty stores are allowed
 * (fail-open); only an explicit `allowed: false` rule from the DB blocks the call.
 */
export function isApiCallAllowed(method: string, endpoint: string): boolean {
  const rule = matchRule(method, endpoint);
  if (!rule) return true;
  if (rule.allowed) return true;
  // Portal / read-only fee users: allow own-invoice list + preview despite coarse api-access rules.
  if (shouldAllowPortalFeeSelfRead(method, endpoint)) return true;
  // Portal students: assignment:submit is granted with screen:portal, not screen:assignments.
  if (shouldAllowPortalAssignmentSubmit(method, endpoint)) return true;
  return false;
}
